(() => {
  if (window.__snugMobileUiFinalLoaded) return;
  window.__snugMobileUiFinalLoaded = true;

  const MOBILE_QUERY = '(max-width: 768px)';
  const standaloneSearch = () => Boolean(window.__snugStandaloneMobileSearch);
  const SEARCH_TRIGGER_SELECTOR = [
    'header-search .search__button',
    'header-search .top-search-toggle',
    'header-search [data-action="toggle-search"]',
    'header-search button[aria-label*="looking" i]',
    'header-search button[aria-label*="search" i]',
  ].join(', ');
  const root = document.documentElement;
  const body = document.body;
  const mobileMedia = window.matchMedia(MOBILE_QUERY);
  let lastMenuTrigger = null;
  let lastSearchTrigger = null;
  let activeSearchHost = null;
  let searchRequested = false;
  let searchReassertTimers = [];
  let syncQueued = false;

  const mobile = () => mobileMedia.matches;
  const visibleSearchHost = () => Array.from(document.querySelectorAll('header-search')).find((host) => {
    const rect = host.getBoundingClientRect();
    const style = window.getComputedStyle(host);
    return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  });
  const searchHost = (source = null) => {
    const triggerHost = source instanceof Element ? source.closest('header-search') : null;
    if (triggerHost) return triggerHost;
    if (searchRequested && activeSearchHost?.isConnected) return activeSearchHost;
    return visibleSearchHost() || document.querySelector('header-search');
  };
  const searchPopup = (host = searchHost()) => host?.querySelector('.popup-search-form');
  const navigation = () => document.querySelector('.navigation.horizontal');
  const menuTrigger = () => document.querySelector('[data-action="toggle-nav"]');
  const cartIsOpen = () => {
    const drawer = document.querySelector('.minicart__wrapper');
    return root.classList.contains('open-minicart') || drawer?.classList.contains('open');
  };
  const searchIsOpen = () => {
    if (standaloneSearch()) return false;
    const popup = searchPopup(activeSearchHost || searchHost());
    return Boolean(
      searchRequested ||
      (popup?.classList.contains('popup-search-show') && !popup.classList.contains('search-hidden'))
    );
  };
  const menuIsOpen = () => body.classList.contains('nav-open');

  const clearSearchReassert = () => {
    searchReassertTimers.forEach((timer) => window.clearTimeout(timer));
    searchReassertTimers = [];
  };

  const applySearchOpen = (host) => {
    if (standaloneSearch() || !host || !searchRequested || !mobile()) return;
    const popup = searchPopup(host);
    const input = host?.querySelector('input[type="search"]');
    if (!host || !popup || !input) return;

    host.classList.add('snug-search-enhanced');
    popup.hidden = false;
    popup.classList.remove('search-hidden', 'invisible');
    popup.classList.add('popup-search-show');
    popup.setAttribute('aria-hidden', 'false');
    root.classList.add('open-search', 'snug-search-open');
    body.classList.add('snug-search-open');
    input.setAttribute('aria-expanded', 'true');
    updateSearchPosition(host);
  };

  const reassertSearchOpen = (host) => {
    clearSearchReassert();
    [0, 60, 180, 420].forEach((delay) => {
      searchReassertTimers.push(window.setTimeout(() => {
        if (!searchRequested || !mobile()) return;
        applySearchOpen(host);
        scheduleSync();
      }, delay));
    });
  };

  const openSearch = ({ focus = true, trigger = null } = {}) => {
    if (standaloneSearch()) return;
    const host = searchHost(trigger);
    const popup = searchPopup(host);
    const input = host?.querySelector('input[type="search"]');
    if (!host || !popup || !input) return;

    if (menuIsOpen()) closeMenu();
    activeSearchHost = host;
    searchRequested = true;
    if (trigger instanceof HTMLElement) lastSearchTrigger = trigger;
    applySearchOpen(host);
    reassertSearchOpen(host);
    scheduleSync();

    if (focus) {
      window.setTimeout(() => {
        if (!searchRequested) return;
        input.focus({ preventScroll: true });
      }, 80);
    }
  };

  const ensureBackdrop = (className) => {
    let backdrop = document.querySelector(`.${className}`);
    if (backdrop) return backdrop;
    backdrop = document.createElement('button');
    backdrop.type = 'button';
    backdrop.className = className;
    backdrop.setAttribute('aria-label', className.includes('search') ? 'Close search' : 'Close menu');
    backdrop.setAttribute('tabindex', '-1');
    body.appendChild(backdrop);
    return backdrop;
  };

  const searchBackdrop = ensureBackdrop('snug-search-backdrop');
  const menuBackdrop = ensureBackdrop('snug-mobile-menu-backdrop');

  const removeStaleDrawerState = () => {
    if (!searchIsOpen() && !menuIsOpen() && !cartIsOpen()) {
      root.classList.remove('open-drawer');
    }
  };

  const closeSearch = ({ focus = false } = {}) => {
    if (standaloneSearch()) return;
    searchRequested = false;
    clearSearchReassert();
    const host = searchHost(activeSearchHost);
    const popup = searchPopup(host);
    const input = host?.querySelector('input[type="search"]');
    popup?.classList.remove('popup-search-show');
    popup?.classList.add('search-hidden');
    popup?.setAttribute('aria-hidden', 'true');
    root.classList.remove('open-search', 'snug-search-open');
    body.classList.remove('snug-search-open');
    input?.setAttribute('aria-expanded', 'false');
    input?.blur();
    removeStaleDrawerState();
    if (focus) (lastSearchTrigger || input)?.focus({ preventScroll: true });
    activeSearchHost = null;
    scheduleSync();
  };

  const closeMenu = ({ focus = false } = {}) => {
    body.classList.remove('nav-open', 'snug-menu-open');
    root.classList.remove('mobile-menu-open', 'snug-menu-open');
    const nav = navigation();
    const toggle = menuTrigger();
    nav?.setAttribute('aria-hidden', 'true');
    toggle?.setAttribute('aria-expanded', 'false');
    toggle?.setAttribute('aria-label', 'Open menu');
    removeStaleDrawerState();
    if (focus) (lastMenuTrigger || toggle)?.focus({ preventScroll: true });
    scheduleSync();
  };

  const updateSearchPosition = (requestedHost = null) => {
    if (standaloneSearch()) return;
    const host = requestedHost || searchHost(activeSearchHost);
    if (!host || !searchIsOpen()) return;
    const header = host.closest('.section-header') || document.querySelector('.section-header') || document.querySelector('header');
    const bottom = Math.max(0, Math.min(window.innerHeight - 120, Math.round(header?.getBoundingClientRect().bottom || 88)));
    host.style.setProperty('--snug-mobile-search-top', `${bottom}px`);
  };

  const ensureChatStyle = () => {
    document.querySelectorAll('shopify-chat').forEach((chat) => {
      const shadow = chat.shadowRoot;
      if (!shadow || shadow.querySelector('#snug-chat-mobile-placement')) return;
      const style = document.createElement('style');
      style.id = 'snug-chat-mobile-placement';
      style.textContent = `
        @media (max-width: 768px) {
          .activator-nudge {
            right: max(12px, env(safe-area-inset-right)) !important;
            bottom: calc(88px + env(safe-area-inset-bottom)) !important;
            left: auto !important;
            transition: transform 220ms ease, opacity 180ms ease !important;
          }

          .activator {
            width: 48px !important;
            min-width: 48px !important;
            height: 48px !important;
            padding: 0 !important;
            border-radius: 16px !important;
            justify-content: center !important;
          }

          .activator__label {
            display: none !important;
          }

          :host(.snug-nav-hidden) .activator-nudge {
            opacity: 0 !important;
            pointer-events: none !important;
            transform: translateY(calc(100% + 28px)) !important;
          }
        }
      `;
      shadow.appendChild(style);
    });
  };

  function syncState() {
    syncQueued = false;
    if (searchRequested && mobile()) applySearchOpen(searchHost(activeSearchHost));
    const searchOpen = searchIsOpen();
    const menuOpen = menuIsOpen();

    if (searchOpen && menuOpen) closeMenu();

    root.classList.toggle('snug-search-open', searchOpen);
    body.classList.toggle('snug-search-open', searchOpen);
    root.classList.toggle('snug-menu-open', menuOpen && !searchOpen);
    body.classList.toggle('snug-menu-open', menuOpen && !searchOpen);
    searchBackdrop.classList.toggle('is-visible', mobile() && searchOpen);
    menuBackdrop.classList.toggle('is-visible', mobile() && menuOpen && !searchOpen);

    const host = searchHost(activeSearchHost);
    const popup = searchPopup(host);
    const input = host?.querySelector('input[type="search"]');
    popup?.setAttribute('aria-hidden', searchOpen ? 'false' : 'true');
    input?.setAttribute('aria-expanded', searchOpen ? 'true' : 'false');

    const nav = navigation();
    const toggle = menuTrigger();
    nav?.setAttribute('aria-hidden', menuOpen ? 'false' : 'true');
    toggle?.setAttribute('aria-expanded', menuOpen ? 'true' : 'false');
    toggle?.setAttribute('aria-label', menuOpen ? 'Close menu' : 'Open menu');

    document.querySelectorAll('shopify-chat').forEach((chat) => {
      chat.classList.toggle('snug-ui-covered', mobile() && (searchOpen || menuOpen || cartIsOpen()));
    });

    if (searchOpen) updateSearchPosition(host);
    if (!searchOpen && !menuOpen) removeStaleDrawerState();
    ensureChatStyle();
  }

  function scheduleSync() {
    if (syncQueued) return;
    syncQueued = true;
    window.requestAnimationFrame(syncState);
  }

  searchBackdrop.addEventListener('click', () => closeSearch({ focus: true }));
  menuBackdrop.addEventListener('click', () => closeMenu({ focus: true }));

  document.addEventListener('pointerdown', (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !mobile()) return;
    const trigger = target.closest(SEARCH_TRIGGER_SELECTOR);
    if (!trigger || trigger.closest('.popup-search-form')) return;
    if (window.__snugStandaloneMobileSearch) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    openSearch({ focus: true, trigger });
  }, { capture: true, passive: false });

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('.snug-search-close, .btn-search-close')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      closeSearch();
      return;
    }

    const searchButton = target.closest(SEARCH_TRIGGER_SELECTOR);
    if (searchButton && mobile()) {
      if (window.__snugStandaloneMobileSearch) return;
      const host = searchHost(searchButton);
      const input = host?.querySelector('input[type="search"]');
      const isPopupSubmit = Boolean(searchButton.closest('.popup-search-form'));
      if (!isPopupSubmit || !searchIsOpen() || !input?.value.trim()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        openSearch({ focus: true, trigger: searchButton });
      }
      return;
    }

    const toggle = target.closest('[data-action="toggle-nav"]');
    if (toggle) {
      lastMenuTrigger = toggle;
      window.setTimeout(scheduleSync, 60);
      return;
    }

    if (target.closest('close-menu.close-menu, .navigation.horizontal .close-menu')) {
      window.setTimeout(() => closeMenu(), 0);
      return;
    }

    if (menuIsOpen() && target.closest('.navigation.horizontal a[href]') && !target.closest('.snug-shop-parent > menu-item > a')) {
      closeMenu();
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (searchIsOpen()) {
      event.preventDefault();
      closeSearch({ focus: true });
    } else if (menuIsOpen()) {
      event.preventDefault();
      closeMenu({ focus: true });
    }
  });

  const observe = () => {
    if (!standaloneSearch()) {
      document.querySelectorAll('header-search .popup-search-form').forEach((popup) => {
        if (popup.dataset.snugFinalObserved === 'true') return;
        popup.dataset.snugFinalObserved = 'true';
        new MutationObserver(scheduleSync).observe(popup, { attributes: true, attributeFilter: ['class', 'aria-hidden'] });
      });
    }
    if (body.dataset.snugFinalObserved !== 'true') {
      body.dataset.snugFinalObserved = 'true';
      new MutationObserver(scheduleSync).observe(body, { attributes: true, attributeFilter: ['class'] });
    }
    ensureChatStyle();
    scheduleSync();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe, { once: true });
  } else {
    observe();
  }

  document.addEventListener('shopify:section:load', observe);
  window.addEventListener('resize', scheduleSync, { passive: true });
  window.addEventListener('orientationchange', scheduleSync, { passive: true });
  mobileMedia.addEventListener?.('change', (event) => {
    if (!event.matches && searchRequested) closeSearch();
    scheduleSync();
  });
  window.addEventListener('scroll', () => {
    if (searchIsOpen()) updateSearchPosition(searchHost(activeSearchHost));
  }, { passive: true });

  const dynamicUiObserver = new MutationObserver((mutations) => {
    const relevant = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => (
      node instanceof Element && (
        node.matches?.('shopify-chat, .section-header') ||
        node.querySelector?.('shopify-chat, .section-header')
      )
    )));
    if (!relevant) return;
    ensureChatStyle();
    observe();
    scheduleSync();
  });
  dynamicUiObserver.observe(body, { childList: true });
})();
